import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/components/admin/DataTable";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const Row = ({ label, children }) => <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm"><dt className="text-slate-600">{label}</dt><dd className="text-right font-medium text-slate-900">{children}</dd></div>;

export default function UserDetail() {
  const { id } = useParams();
  const api = useApi();
  const { me } = useMe();
  const can = (p) => me.user.permissions.includes(p);
  const [d, setD] = useState(null);
  const [roles, setRoles] = useState([]);
  const [name, setName] = useState("");
  const [dlg, setDlg] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => api.get(`/admin/users/${id}`).then((r) => { setD(r.data); setName(r.data.user.name); }).catch((e) => toast.error(errMsg(e))), [api, id]);
  useEffect(() => { load(); api.get("/admin/roles").then((r) => setRoles(r.data.roles)).catch(() => {}); }, [load, api]);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); setDlg(null); setReason(""); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!d) return <Spinner />;
  const u = d.user;
  const self = u.user_id === me.user.user_id;
  const roleOpts = roles.filter((r) => me.user.role === "super_admin" || !r.is_admin);

  return (
    <div className="max-w-5xl">
      <Link to="/admin/users" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-700 hover:text-[#222d99]"><ArrowLeft className="h-4 w-4" /> Users</Link>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-testid="user-detail-name">{u.name}</h1>
        <Badge className={u.status === "suspended" ? "bg-rose-700" : "bg-emerald-700"} data-testid="user-detail-status">{u.status || "active"}</Badge>
        {u.is_test && <Badge variant="outline">Test data</Badge>}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="mb-2 font-semibold text-slate-900">Details</h2>
          <dl>
            <Row label="Email">{u.email}</Row>
            <Row label="Role">{roles.find((r) => r.key === u.role)?.name || u.role}</Row>
            <Row label="Email verified">{d.clerk.unavailable ? "Unavailable" : d.clerk.email_verified ? "Yes" : "No"}</Row>
            <Row label="Two-factor">{d.clerk.unavailable ? "Unavailable" : d.clerk.two_factor_enabled ? "On" : "Off"}</Row>
            <Row label="Account verified by admin">{u.identity_verified ? "Yes" : "No"}</Row>
            <Row label="Joined">{fmtDate(u.created_at)}</Row>
            <Row label="Last active">{fmtDate(u.last_seen_at)}</Row>
            {u.suspension_reason && <Row label="Suspension reason">{u.suspension_reason}</Row>}
          </dl>
        </section>
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="user-actions">
          <h2 className="font-semibold text-slate-900">Actions</h2>
          {can("users.edit") && (
            <div className="flex gap-2"><div className="flex-1 space-y-1.5"><Label htmlFor="ud-name">Display name</Label><Input id="ud-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="ud-name" /></div>
              <Button className="self-end" variant="outline" onClick={() => act(() => api.patch(`/admin/users/${id}`, { name }), "Profile saved")} data-testid="ud-save-name">Save</Button></div>
          )}
          {can("users.roles") && !self && (
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={u.role} onValueChange={(v) => act(() => api.patch(`/admin/users/${id}/role`, { role: v }), "Role changed")}>
                <SelectTrigger data-testid="ud-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roleOpts.map((r) => <SelectItem key={r.key} value={r.key}>{r.name}</SelectItem>)}</SelectContent></Select>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {can("users.edit") && !u.identity_verified && <Button variant="outline" onClick={() => act(() => api.post(`/admin/users/${id}/verify`), "Account verified")} data-testid="ud-verify">Verify account</Button>}
            {can("users.recovery") && <Button variant="outline" onClick={() => setDlg("reset")} data-testid="ud-reset">Trigger password reset</Button>}
            {can("users.suspend") && !self && (u.status === "suspended"
              ? <Button variant="outline" onClick={() => act(() => api.post(`/admin/users/${id}/reinstate`), "User reinstated")} data-testid="ud-reinstate">Reinstate</Button>
              : <Button variant="destructive" onClick={() => setDlg("suspend")} data-testid="ud-suspend">Suspend</Button>)}
          </div>
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="mb-2 font-semibold text-slate-900">Enrolments</h2>
          <ul className="space-y-1 text-sm">{d.enrolments.map((e) => <li key={e.id}>{e.title} <span className="text-slate-600">· {e.completed_at ? "completed" : "in progress"}</span></li>)}{!d.enrolments.length && <li className="text-slate-600">No data yet</li>}</ul>
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="mb-2 font-semibold text-slate-900">Activity</h2>
          <ul className="space-y-1 text-sm">{d.activity.map((a) => <li key={a.id}><span className="font-medium">{a.action}</span> <span className="text-slate-600">· {a.actor_email} · {fmtDate(a.created_at)}</span></li>)}{!d.activity.length && <li className="text-slate-600">No data yet</li>}</ul>
        </section>
      </div>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dlg === "suspend" ? `Suspend ${u.name}?` : `Sign ${u.name} out and send reset instructions?`}</DialogTitle></DialogHeader>
          {dlg === "suspend" && <div className="space-y-1.5"><Label htmlFor="ud-reason">Reason (required)</Label><Textarea id="ud-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="ud-reason" /></div>}
          <DialogFooter>
            <Button variant={dlg === "suspend" ? "destructive" : "default"} data-testid="ud-confirm" disabled={dlg === "suspend" && reason.trim().length < 5}
              onClick={() => dlg === "suspend" ? act(() => api.post(`/admin/users/${id}/suspend`, { reason }), "User suspended") : act(() => api.post(`/admin/users/${id}/password-reset`), "Signed out; reset instructions emailed")}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
